import { useEffect, useState } from "react";
import axios from "axios";
import { useParams, Link } from "react-router-dom";
import { ExternalLink, Calendar as CalendarIcon, Clock, MapPin, ArrowLeft } from "lucide-react";
import SEOHead from "@/components/layout/SEOHead";
import { PageHero } from "@/pages/About.hero";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function EventDetail() {
  const { slug } = useParams();
  const [ev, setEv] = useState(null);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    axios.get(`${API}/events/${encodeURIComponent(slug)}`)
      .then((r) => { if (alive) setEv(r.data); })
      .catch(() => { if (alive) setError(true); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [slug]);

  if (loading) {
    return (
      <section className="bg-white py-24">
        <div className="max-w-3xl mx-auto px-5 text-center text-[var(--muted)]">
          Loading event...
        </div>
      </section>
    );
  }

  if (error || !ev) {
    return (
      <>
        <SEOHead title="Event not found" description="This event could not be found." path={`/events/${slug}`} />
        <PageHero eyebrow="Events" title="Event not found" subtitle="Sorry - we couldn't find the event you're looking for." />
        <section className="bg-white py-16">
          <div className="max-w-2xl mx-auto px-5 text-center">
            <Link to="/events" className="btn-primary">
              <ArrowLeft size={16} /> See all events
            </Link>
          </div>
        </section>
      </>
    );
  }

  const flyer = ev.flyer_url && ev.flyer_url.startsWith("/api/")
    ? `${process.env.REACT_APP_BACKEND_URL}${ev.flyer_url}`
    : ev.flyer_url;

  const rsvpUrl = ev.rsvp_url || ev.payment_url || ev.details_url;
  const rsvpLabel =
    ev.rsvp_url && ev.payment_url
      ? "RSVP and Pay"
      : ev.rsvp_url
      ? "RSVP for this event"
      : ev.payment_url
      ? "Pay for this event"
      : "Learn more";

  const formattedDate = ev.start_date
    ? new Date(ev.start_date + "T12:00:00").toLocaleDateString("en-US", {
        weekday: "long", month: "long", day: "numeric", year: "numeric",
      })
    : null;

  return (
    <>
      <SEOHead
        title={ev.title}
        description={ev.description?.slice(0, 200)}
        path={`/events/${ev.slug || ev.id}`}
        ogImage={flyer}
      />
      <PageHero
        eyebrow={ev.category || "Event"}
        title={ev.title}
        subtitle={[formattedDate, ev.start_time && (ev.end_time ? `${ev.start_time} - ${ev.end_time}` : ev.start_time)].filter(Boolean).join(" · ")}
      />

      <section className="bg-white py-12 lg:py-16">
        <div className="max-w-5xl mx-auto px-5 lg:px-10 grid md:grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)] gap-8 md:gap-12">
          {/* Flyer */}
          {flyer && (
            <a
              href={flyer}
              target="_blank"
              rel="noopener noreferrer"
              className="block rounded-2xl overflow-hidden border border-[var(--line)] shadow-md hover:shadow-2xl transition-shadow bg-white"
              data-testid="event-detail-flyer-link"
            >
              <img
                src={flyer}
                alt={`Flyer for ${ev.title}`}
                className="w-full h-auto block"
                data-testid="event-detail-flyer"
              />
            </a>
          )}

          {/* Details */}
          <div>
            <div className="space-y-2 text-[var(--ink-soft)] mb-6">
              {formattedDate && (
                <div className="flex items-center gap-2 text-sm"><CalendarIcon size={14} className="text-[var(--blue)]" /> {formattedDate}</div>
              )}
              {ev.start_time && (
                <div className="flex items-center gap-2 text-sm"><Clock size={14} className="text-[var(--blue)]" /> {ev.start_time}{ev.end_time ? ` - ${ev.end_time}` : ""}</div>
              )}
              {ev.location && (
                <div className="flex items-center gap-2 text-sm"><MapPin size={14} className="text-[var(--blue)]" /> {ev.location}</div>
              )}
            </div>

            {ev.description && (
              <p className="text-[var(--ink)] leading-relaxed whitespace-pre-wrap mb-8" data-testid="event-detail-description">
                {ev.description}
              </p>
            )}

            <div className="flex flex-wrap gap-3">
              {rsvpUrl && (
                <a
                  href={rsvpUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-primary"
                  data-testid="event-detail-rsvp"
                >
                  {rsvpLabel} <ExternalLink size={14} />
                </a>
              )}
              {ev.details_url && ev.details_url !== rsvpUrl && (
                <a
                  href={ev.details_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-ghost"
                >
                  Event details <ExternalLink size={14} />
                </a>
              )}
              <Link to="/events" className="btn-ghost">
                <ArrowLeft size={14} /> All events
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
